import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CheckCircle } from 'lucide-react';
import { authApiV2 } from '@/api/endpoints/auth';
import { dashboardApi } from '@/api/endpoints/dashboard';
import { dashboardAccessContextQueryKey } from '@/features/dashboard/hooks/useDashboardAccessContext';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { DEFAULT_POST_AUTH_PATH } from '@/lib/default-landing';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth.store';

const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetFormData = z.infer<typeof resetSchema>;

type TokenCheckState = 'idle' | 'checking' | 'valid' | 'invalid';

function resetErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = body?.error?.code;
    const message = body?.error?.message;
    if (code === 'password_reused' && message) return message;
    if (code === 'invalid_reset_token' && message) return message;
    if (code === 'rate_limited' || error.response?.status === 429) {
      return 'Too many attempts. Please wait a few minutes and try again.';
    }
    if (message) return message;
  }
  return 'Unable to reset your password. The link may have expired. Please request a new one.';
}

function BackToSignInLink() {
  return (
    <Link
      to="/login"
      className="font-medium text-primary hover:text-primary hover:underline"
    >
      Back to sign in
    </Link>
  );
}

function InvalidResetLink() {
  return (
    <AuthMarketingShell
      title="Invalid reset link"
      subtitle="This password reset link is invalid or has expired."
      footer={<BackToSignInLink />}
    >
      <Link
        to="/forgot-password"
        className="block w-full rounded-md bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        Request a new reset
      </Link>
    </AuthMarketingShell>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTokens, setUser, setPendingTwoFactor } = useAuthStore();
  // Phase 2A: the /api/auth/password/reset endpoint only needs the token.
  // `email` is still rendered (if present) as a hint for the user.
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';

  const [successManualSignIn, setSuccessManualSignIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenCheck, setTokenCheck] = useState<TokenCheckState>(
    token ? 'checking' : 'invalid',
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const passwordValue = watch('password') ?? '';
  const confirmPasswordValue = watch('confirmPassword') ?? '';

  // DEF-241 — pre-validate the reset token before rendering the form.
  useEffect(() => {
    if (!token) {
      setTokenCheck('invalid');
      return;
    }
    let cancelled = false;
    setTokenCheck('checking');
    void authApiV2
      .verifyResetToken({ token })
      .then((result) => {
        if (!cancelled) setTokenCheck(result.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setTokenCheck('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(data: ResetFormData) {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await authApiV2.resetPassword({
        token,
        newPassword: data.password,
      });

      // DEF-292 — MFA-enabled: route to challenge (never auto-sign-in).
      if ('requiresMfa' in result && result.requiresMfa === true) {
        const codeSent = Boolean(result.channel);
        const method =
          result.channel === 'sms'
            ? 'sms'
            : result.channel === 'email'
              ? 'email'
              : (result.availableChannels?.[0] ?? 'email');
        setPendingTwoFactor({
          email: result.user?.email ?? email,
          userId: result.userId,
          method,
          phoneMasked: result.phoneMasked ?? '***-***-****',
          mfaChallengeToken: result.mfaChallengeToken,
          availableChannels: result.availableChannels,
          codeSent,
          devSecret: result.devSecret,
        });
        navigate('/mfa/verify', { replace: true });
        return;
      }

      // DEF-292 — MFA off: land in-app with minted session.
      if (
        'requiresMfa' in result &&
        result.requiresMfa === false &&
        result.accessToken &&
        result.refreshToken
      ) {
        setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        if (isSupabaseLoginEnabled()) {
          await getSupabaseClient().auth.setSession({
            access_token: result.accessToken,
            refresh_token: result.refreshToken,
          });
        }
        try {
          const session = await authApiV2.me();
          setUser(session);
          queryClient.setQueryData(['auth', 'me'], session);
          queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
          await queryClient.fetchQuery({
            queryKey: dashboardAccessContextQueryKey(session.userId),
            queryFn: () => dashboardApi.getAccessContext(),
          });
        } catch {
          if (result.user) {
            setUser({
              userId: result.user.userId,
              temporaryUsersId: null,
              username: result.user.email,
              email: result.user.email,
              token: result.accessToken,
              role: result.user.role ?? '',
              roleId: 0,
              companyId: result.user.companyId ?? 0,
              companyName: result.user.companyName ?? '',
              firstName: result.user.firstName ?? '',
              lastName: result.user.lastName ?? '',
              name: `${result.user.firstName ?? ''} ${result.user.lastName ?? ''}`.trim(),
              isActiveDirectory: false,
              isSessionActivated: true,
              isApproved: true,
              active: true,
              authProvider: result.user.authProvider ?? 'local',
            });
          }
        }
        navigate(DEFAULT_POST_AUTH_PATH, { replace: true });
        return;
      }

      // Mint failed — fall back to manual sign-in.
      setSuccessManualSignIn(true);
    } catch (err) {
      setError(resetErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  if (!token || tokenCheck === 'invalid') {
    return <InvalidResetLink />;
  }

  if (tokenCheck === 'checking') {
    return <AuthMarketingShell isLoading />;
  }

  if (successManualSignIn) {
    return (
      <AuthMarketingShell
        title="Password reset successful"
        subtitle="Your password has been updated. You can now sign in with your new password."
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <CheckCircle className="h-6 w-6 text-primary" />
          </div>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-2 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Sign in
          </button>
        </div>
      </AuthMarketingShell>
    );
  }

  return (
    <AuthMarketingShell
      title="Set new password"
      subtitle={
        email
          ? `Choose a strong password for ${email}`
          : 'Choose a strong password for your account.'
      }
      footer={<BackToSignInLink />}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        className="flex flex-col gap-5"
      >
        {error && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
            New password
          </label>
          <AuthTextField
            {...register('password')}
            id="password"
            autoComplete="off"
            autoFocus
            value={passwordValue}
            onClear={() =>
              setValue('password', '', { shouldDirty: true, shouldValidate: true })
            }
            clearLabel="Clear password"
            passwordToggle
            isPasswordVisible={showPassword}
            onTogglePasswordVisibility={() => setShowPassword((v) => !v)}
            isInvalid={!!errors.password}
            placeholder="At least 8 characters"
          />
          {errors.password && (
            <p className="mt-1 text-xs text-destructive-text">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-foreground">
            Confirm new password
          </label>
          <AuthTextField
            {...register('confirmPassword')}
            id="confirmPassword"
            autoComplete="off"
            value={confirmPasswordValue}
            onClear={() =>
              setValue('confirmPassword', '', { shouldDirty: true, shouldValidate: true })
            }
            clearLabel="Clear confirm password"
            passwordToggle
            isPasswordVisible={showConfirmPassword}
            onTogglePasswordVisibility={() => setShowConfirmPassword((v) => !v)}
            isInvalid={!!errors.confirmPassword}
            placeholder="Re-enter your password"
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-destructive-text">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
          <p className="font-medium text-muted-foreground">Password requirements:</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            <li>At least 8 characters</li>
            <li>One uppercase letter</li>
            <li>One lowercase letter</li>
            <li>One number</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? 'Resetting…' : 'Reset password'}
        </button>
      </form>
    </AuthMarketingShell>
  );
}
